import { useState, useCallback, useEffect, useRef } from 'react'
import type { Camera } from '../../types'
import { downloadSnapshot, configureAlarmCenter } from '../../lib/cameraApi'
import { supabase } from '../../lib/supabase'
import { LivePlayer } from '../../lib/mseLivePlayer'
import { PlaybackTimeline } from './PlaybackTimeline'

interface Props {
  camera: Camera | null
  index: number
  isSelected: boolean
  onSelect: () => void
  onClear: () => void
  onToggleHd: (camera: Camera, isHd: boolean) => void
  onPtzOpen: (camera: Camera) => void
  onRecordingOpen: (camera: Camera) => void
  onEditCamera: (camera: Camera) => void
  onSettingsOpen: (camera: Camera) => void
  onDrop: (cameraId: string) => void
  style?: { gridColumn: string; gridRow: string }
}

interface ContextMenuPos {
  x: number
  y: number
}

export function StreamSlot({
  camera,
  index,
  isSelected,
  onSelect,
  onClear,
  onToggleHd,
  onPtzOpen,
  onRecordingOpen,
  onEditCamera,
  onSettingsOpen,
  onDrop,
  style,
}: Props) {
  const [hovered, setHovered] = useState(false)
  const [isHd, setIsHd] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [ctxMenu, setCtxMenu] = useState<ContextMenuPos | null>(null)
  const [streamError, setStreamError] = useState<string | null>(null)
  const [playbackMode, setPlaybackMode] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<LivePlayer | null>(null)

  // Streaming requires either a direct external IP or a gateway (droplet) relay
  const canStream = camera && (camera.gateway_id || camera.external_ip)

  // Start/restart stream when camera or quality changes
  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.stop()
      playerRef.current = null
    }
    setStreamError(null)

    if (!camera || !canStream || !containerRef.current || playbackMode) return

    const container = containerRef.current
    let cancelled = false
    const streamId = isHd ? 1 : 2 // 1 = main/HD, 2 = sub/SD

    // Get Supabase token and connect to WSS proxy
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled || !session) {
        if (!cancelled) setStreamError('Not authenticated')
        return
      }

      // Build WSS proxy URL — server handles camera auth + protocol
      const proto = location.protocol === 'https:' ? 'wss' : 'ws'
      const wsUrl = `${proto}://${location.host}/api/camera/ws-stream?cameraId=${camera.id}&streamId=${streamId}&token=${encodeURIComponent(session.access_token)}`

      const player = new LivePlayer({
        container,
        wsUrl,
        isHd,
        onError: (err) => {
          if (!cancelled) {
            console.error('Stream error:', err)
            setStreamError(err.message ?? 'Stream failed')
          }
        },
      })

      playerRef.current = player
      player.start()

      // Auto-configure alarm center once per camera per session
      // so the camera pushes AI events (face/body/vehicle/plate) to our endpoint
      const alarmKey = `rv-alarm-configured-${camera.id}`
      if (!sessionStorage.getItem(alarmKey)) {
        sessionStorage.setItem(alarmKey, '1')
        configureAlarmCenter(camera.id).catch(() => {})
      }
    })

    return () => {
      cancelled = true
      if (playerRef.current) {
        playerRef.current.stop()
        playerRef.current = null
      }
    }
  }, [camera?.id, canStream, isHd, playbackMode]) // eslint-disable-line react-hooks/exhaustive-deps

  // Close context menu on outside click or escape
  useEffect(() => {
    if (!ctxMenu) return
    function handleClick(e: MouseEvent) {
      // Don't close if clicking inside the menu — let button onClick fire first
      if (menuRef.current && menuRef.current.contains(e.target as Node)) return
      setCtxMenu(null)
    }
    function handleKey(e: KeyboardEvent) { if (e.key === 'Escape') setCtxMenu(null) }
    document.addEventListener('click', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('click', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [ctxMenu])

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    if (!camera) return
    e.preventDefault()
    e.stopPropagation()
    setCtxMenu({ x: e.clientX, y: e.clientY })
  }, [camera])

  const handleToggleHd = useCallback(() => {
    if (!camera) return
    const next = !isHd
    setIsHd(next)
    onToggleHd(camera, next)
  }, [camera, isHd, onToggleHd])

  const handleDoubleClick = useCallback(() => {
    const el = document.getElementById(`slot-${index}`)
    if (el) {
      if (document.fullscreenElement) {
        document.exitFullscreen()
      } else {
        el.requestFullscreen()
      }
    }
  }, [index])

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(true)
  }

  function handleDragLeave() {
    setDragOver(false)
  }

  function handleDropEvent(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    const cameraId = e.dataTransfer.getData('text/camera-id')
    if (cameraId) onDrop(cameraId)
  }

  const ctxItemCls = 'w-full text-left px-3 py-1.5 text-xs hover:bg-white/10 transition-colors flex items-center gap-2'

  return (
    <div
      id={`slot-${index}`}
      className={`relative bg-black rounded-lg overflow-hidden border-2 transition-colors min-h-0 ${
        dragOver ? 'border-brand' :
        isSelected ? 'border-brand/50' :
        'border-transparent'
      }`}
      style={style}
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDropEvent}
    >
      {camera && canStream ? (
        <div className="w-full h-full relative">
          <div
            ref={containerRef}
            className={`w-full h-full bg-black ${playbackMode ? 'hidden' : ''}`}
          />
          {streamError && !playbackMode && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500 bg-black/80">
              <p className="text-xs">{streamError}</p>
              <button
                className="text-xs text-brand mt-2 hover:underline"
                onClick={(e) => {
                  e.stopPropagation()
                  setStreamError(null)
                  setIsHd(h => !h)
                  setTimeout(() => setIsHd(h => !h), 100)
                }}
              >
                Retry
              </button>
            </div>
          )}
          {playbackMode && camera && (
            <PlaybackTimeline camera={camera} onClose={() => setPlaybackMode(false)} />
          )}
        </div>
      ) : camera ? (
        /* Camera assigned but not streamable */
        <div className="w-full h-full flex flex-col items-center justify-center text-gray-600">
          <p className="text-sm font-medium text-gray-400">{camera.name}</p>
          <p className="text-xs mt-1">{camera.cam_ip}:{camera.port}</p>
          <p className="text-xs mt-3 text-gray-700">Live streaming requires a relay server</p>
        </div>
      ) : (
        /* Empty slot */
        <div className="w-full h-full flex items-center justify-center text-gray-700 text-sm">
          {dragOver ? 'Drop camera here' : 'Empty'}
        </div>
      )}

      {/* Hover overlay controls */}
      {camera && hovered && (
        <>
          {/* Stream label — top left */}
          <div className="absolute top-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
            {camera.name}
          </div>

          {/* Controls — top right */}
          <div className="absolute top-2 right-2 flex gap-1">
            {canStream && (
              <button
                onClick={(e) => { e.stopPropagation(); handleToggleHd() }}
                className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                  isHd ? 'bg-info text-white' : 'bg-black/70 text-gray-300 hover:text-white'
                }`}
              >
                {isHd ? 'HD' : 'SD'}
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onPtzOpen(camera) }}
              className="bg-black/70 text-gray-300 hover:text-white text-xs px-2 py-1 rounded transition-colors"
            >
              PTZ
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setPlaybackMode(true) }}
              className={`text-xs px-2 py-1 rounded font-medium transition-colors ${
                playbackMode ? 'bg-warning text-white' : 'bg-black/70 text-gray-300 hover:text-white'
              }`}
            >
              {playbackMode ? 'LIVE' : 'PLAY'}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onRecordingOpen(camera) }}
              className="bg-black/70 text-gray-300 hover:text-white text-xs px-2 py-1 rounded transition-colors"
            >
              REC
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); onClear() }}
              className="bg-black/70 text-gray-300 hover:text-error text-xs px-2 py-1 rounded transition-colors"
            >
              &times;
            </button>
          </div>
        </>
      )}

      {/* Right-click context menu */}
      {ctxMenu && camera && (
        <div
          ref={menuRef}
          className="fixed z-50 bg-gray-900 border border-white/10 rounded-lg shadow-xl py-1 min-w-[180px] text-gray-200"
          style={{ left: ctxMenu.x, top: ctxMenu.y }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-500 border-b border-white/10 mb-1">
            {camera.name}
          </div>

          <button
            className={ctxItemCls}
            onClick={() => { setCtxMenu(null); onEditCamera(camera) }}
          >
            <span className="w-4 text-center text-gray-500">&#9998;</span>
            Edit Camera
          </button>

          <button
            className={ctxItemCls}
            onClick={() => { setCtxMenu(null); onPtzOpen(camera) }}
          >
            <span className="w-4 text-center text-gray-500">&#8982;</span>
            PTZ Controls
          </button>

          <button
            className={ctxItemCls}
            onClick={() => { setCtxMenu(null); setPlaybackMode(true) }}
          >
            <span className="w-4 text-center text-gray-500">&#9654;</span>
            Archive Playback
          </button>

          <button
            className={ctxItemCls}
            onClick={() => { setCtxMenu(null); onRecordingOpen(camera) }}
          >
            <span className="w-4 text-center text-gray-500">&#9679;</span>
            Recordings
          </button>

          <button
            className={ctxItemCls}
            onClick={() => { setCtxMenu(null); onSettingsOpen(camera) }}
          >
            <span className="w-4 text-center text-gray-500">&#9881;</span>
            Camera Settings
          </button>

          {canStream && (
            <button
              className={ctxItemCls}
              onClick={() => { setCtxMenu(null); handleToggleHd() }}
            >
              <span className="w-4 text-center text-gray-500">&#9632;</span>
              Switch to {isHd ? 'SD' : 'HD'}
            </button>
          )}

          <button
            className={ctxItemCls}
            onClick={() => {
              setCtxMenu(null)
              downloadSnapshot(camera.id, camera.name).catch(() => {})
            }}
          >
            <span className="w-4 text-center text-gray-500">&#8681;</span>
            Save Snapshot
          </button>

          <button
            className={ctxItemCls}
            onClick={() => { setCtxMenu(null); handleDoubleClick() }}
          >
            <span className="w-4 text-center text-gray-500">&#9974;</span>
            Fullscreen
          </button>

          <div className="border-t border-white/10 mt-1 pt-1">
            <button
              className={`${ctxItemCls} text-red-400 hover:text-red-300`}
              onClick={() => { setCtxMenu(null); onClear() }}
            >
              <span className="w-4 text-center">&#10005;</span>
              Remove from Grid
            </button>
          </div>

          <div className="px-3 pt-1.5 pb-1 border-t border-white/10 mt-1">
            <p className="text-[10px] text-gray-600 font-mono">{camera.cam_ip}:{camera.port}</p>
          </div>
        </div>
      )}
    </div>
  )
}
